// Flujo de aprobación interna de cotizaciones (FIRMAELECTRONICA RF-001/RF-002).
// La aprobación es OPCIONAL: publicar directo desde Borrador sigue funcionando.
// Transiciones: Borrador → (solicitar) → Pendiente de Aprobación → APPROVED → Aprobada;
// REJECTED / CHANGES_REQUESTED devuelven la cotización a Borrador.

/** Decisión posible del aprobador. */
export type QuoteApprovalDecision = 'APPROVED' | 'REJECTED' | 'CHANGES_REQUESTED';

/** Estado de una solicitud de aprobación. */
export type QuoteApprovalStatus = 'PENDING' | QuoteApprovalDecision;

/** Estado de cotización mientras espera la aprobación interna (id 9 en dev). */
export const QUOTE_STATUS_PENDING_APPROVAL = 'Pendiente de Aprobación';
/** Estado de cotización aprobada internamente, lista para publicar/enviar (id 10 en dev). */
export const QUOTE_STATUS_APPROVED_INTERNAL = 'Aprobada';

/** Etiquetas legibles en español por estado de la solicitud. */
export const QUOTE_APPROVAL_STATUS_LABELS: Record<QuoteApprovalStatus, string> = {
  PENDING: 'Pendiente',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  CHANGES_REQUESTED: 'Cambios solicitados'
};

/** Verbos de acción para los botones de decisión. */
export const QUOTE_APPROVAL_DECISION_ACTION_LABELS: Record<QuoteApprovalDecision, string> = {
  APPROVED: 'Aprobar',
  REJECTED: 'Rechazar',
  CHANGES_REQUESTED: 'Solicitar cambios'
};

/** Clases Tailwind del chip por estado (PENDING=violeta, APPROVED=verde, REJECTED=rojo, CHANGES_REQUESTED=ámbar). */
export const QUOTE_APPROVAL_STATUS_CHIP: Record<QuoteApprovalStatus, string> = {
  PENDING: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  APPROVED: 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400',
  REJECTED: 'bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400',
  CHANGES_REQUESTED: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
};

/** Devuelve la etiqueta legible de un estado de aprobación (o el valor crudo si no está catalogado). */
export function quoteApprovalStatusLabel(status?: string | null): string {
  if (!status) return '';
  return QUOTE_APPROVAL_STATUS_LABELS[status as QuoteApprovalStatus] || status;
}

/** Clases del chip para un estado de aprobación (gris si no está catalogado). */
export function quoteApprovalStatusChip(status?: string | null): string {
  return QUOTE_APPROVAL_STATUS_CHIP[status as QuoteApprovalStatus]
    || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
}

/** "desde el CRM" / "desde el correo" según el canal por el que se decidió. */
export function quoteApprovalDecidedViaLabel(via?: string | null): string {
  if (via === 'CRM') return 'desde el CRM';
  if (via === 'EMAIL') return 'desde el correo';
  return '';
}

// POST /api/quotes/{id}/request-approval — body
export interface QuoteApprovalRequest {
  /** Usuario ACTIVO del mismo business y distinto del solicitante. */
  approverUserId: number;
  /** Opcional, máx 2000 caracteres. */
  comment?: string;
}

// POST /api/quote-approvals/{approvalId}/decide y POST público .../decide — body
export interface QuoteApprovalDecideRequest {
  decision: QuoteApprovalDecision;
  /** Opcional, máx 2000 caracteres. */
  comment?: string;
}

// Shape de respuesta de request-approval, decide y GET /api/quotes/{id}/approvals
export interface QuoteApprovalResponse {
  id: number;
  quoteId: number;
  quoteNumber: string;
  businessId: number;
  requestedBy: number;
  requesterName: string;
  approverId: number;
  approverName: string;
  requestComment?: string | null;
  status: QuoteApprovalStatus;
  decisionComment?: string | null;
  decidedVia?: 'CRM' | 'EMAIL' | null;
  datetimeRequested: string;
  datetimeDecided?: string | null;
}

// GET /api/quote-approvals/pending — bandeja del aprobador (usuario del token)
export interface QuoteApprovalPendingItem {
  id: number;
  quoteId: number;
  quoteNumber: string;
  version: number;
  dealName?: string | null;
  companyName?: string | null;
  totalAmount: number;
  currencyCode?: string | null;
  requestedBy: number;
  requesterName: string;
  requestComment?: string | null;
  datetimeRequested: string;
}

// GET /api/quote-approvals/public/{token} — resumen para decidir sin JWT
export interface QuoteApprovalPublicResponse {
  status: QuoteApprovalStatus;
  quoteNumber: string;
  version: number;
  dealName?: string | null;
  companyName?: string | null;
  contactName?: string | null;
  totalAmount: number;
  currencyCode?: string | null;
  globalDiscount?: number | null;
  globalDiscountType?: 'percent' | 'fixed' | null;
  requesterName: string;
  requestComment?: string | null;
  datetimeRequested: string;
  datetimeDecided?: string | null;
  decisionComment?: string | null;
}
